import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, X, ArrowLeft } from "lucide-react";
import Header from "../Header";
import Footer from "../Footer";
import WhatsAppFloat from "../WhatsAppFloat";
import PageMeta from "../PageMeta";
import { getComparisonBySlug, comparisonPages } from "../../data/pageConfig";

export default function ComparisonPage() {
  const { compareSlug, lang } = useParams();
  const { t } = useTranslation("comparisons");
  const navigate = useNavigate();
  const comparison = getComparisonBySlug(compareSlug);

  if (!comparison) {
    navigate(`/${lang || "en"}`, { replace: true });
    return null;
  }

  const prefix = lang ? `/${lang}` : "";
  const rows = t(`${comparison.key}.table`, { returnObjects: true });
  const reasons = t(`${comparison.key}.reasons`, { returnObjects: true });

  return (
    <div className="min-h-screen bg-white">
      <PageMeta
        title={`${t(`${comparison.key}.title`)} — TrackTeam`}
        description={t(`${comparison.key}.description`)}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-24 bg-gradient-to-br from-slate-50 via-blue-50/40 to-purple-50/30 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
          <Link to={`${prefix}`} className="inline-flex items-center gap-2 text-[#62329f] hover:underline mb-6 text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            {t("backToHome")}
          </Link>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
            {t(`${comparison.key}.title`)}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl leading-relaxed">
            {t(`${comparison.key}.description`)}
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t(`${comparison.key}.tableTitle`)}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 text-gray-600 font-medium">{t("feature")}</th>
                  <th className="text-center py-4 px-4 text-[#62329f] font-bold">TrackTeam</th>
                  <th className="text-center py-4 px-4 text-gray-600 font-bold">{comparison.competitor}</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(rows) &&
                  rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                      <td className="py-4 px-4 text-gray-700 font-medium">{row.feature}</td>
                      <td className="py-4 px-4 text-center">
                        {row.trackteam === true ? (
                          <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                        ) : row.trackteam === false ? (
                          <X className="w-6 h-6 text-red-400 mx-auto" />
                        ) : (
                          <span className="text-gray-700 text-sm">{row.trackteam}</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {row.competitor === true ? (
                          <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                        ) : row.competitor === false ? (
                          <X className="w-6 h-6 text-red-400 mx-auto" />
                        ) : (
                          <span className="text-gray-500 text-sm">{row.competitor}</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Reasons to Switch */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            {t(`${comparison.key}.reasonsTitle`)}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array.isArray(reasons) &&
              reasons.map((reason, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{reason.title}</h3>
                  <p className="text-gray-600">{reason.text}</p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Other Comparisons */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t("otherComparisons")}</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {comparisonPages
              .filter((c) => c.slug !== compareSlug)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`${prefix}/compare/${c.slug}`}
                  className="px-4 py-2 rounded-full border border-gray-200 hover:border-[#62329f] hover:text-[#62329f] transition-colors text-sm font-medium"
                >
                  TrackTeam vs {c.competitor}
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#62329f] via-purple-700 to-indigo-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t(`${comparison.key}.ctaTitle`)}</h2>
          <p className="text-lg text-purple-100 mb-8 max-w-2xl mx-auto">{t(`${comparison.key}.ctaSubtitle`)}</p>
          <div className="flex justify-center gap-4">
            <a href="https://apps.apple.com/ae/app/trackteam/id6755401884" target="_blank" rel="noreferrer">
              <img src="/Image/logo_appstore.svg" alt="App Store" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share" target="_blank" rel="noreferrer">
              <img src="/Image/logo_playstore.svg" alt="Google Play" className="h-12 w-[162px] object-contain" loading="lazy" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
